/*
 * Bundle add-ons on sofa pages.
 * - Colour follows the sofa's selected colour until the shopper picks one for that add-on.
 * - Ticked add-ons go into the cart in the same request as the sofa (one add-to-cart click).
 * - "Buy it now" is hidden while add-ons are ticked, because it would skip them.
 */
(() => {
  const ADDON_PROPERTY = '_bundle_addon';
  const PARENT_PROPERTY = '_bundle_parent';

  const formatMoney = (cents, format) => {
    const template = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    const value = (Number(cents) || 0) / 100;
    const build = (decimals, thousands = ',', decimal = '.') => {
      const parts = value.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return template.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return build(0);
        case 'amount_with_comma_separator':
          return build(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return build(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return build(2, "'");
        default:
          return build(2);
      }
    });
  };

  const readJson = (element) => {
    try {
      return JSON.parse(element.textContent);
    } catch (e) {
      return null;
    }
  };

  class BundleAddons {
    constructor(root) {
      this.root = root;
      this.sofa = readJson(root.querySelector('[data-sofa-variants]')) || { variants: {} };
      this.sofaVariantIds = new Set(Object.keys(this.sofa.variants).map(Number));
      this.summary = root.querySelector('[data-addons-summary]');
      this.error = root.querySelector('[data-addons-error]');
      this.container = root.closest('.product__info-container, .product__info-wrapper, section') || document;

      this.addons = [...root.querySelectorAll('[data-addon]')].map((element) => {
        const variants = readJson(element.querySelector('[data-addon-variants]')) || [];
        return {
          element,
          variants,
          checkbox: element.querySelector('[data-addon-check]'),
          image: element.querySelector('[data-addon-image]'),
          price: element.querySelector('[data-addon-price]'),
          compare: element.querySelector('[data-addon-compare]'),
          qtyLabel: element.querySelector('[data-addon-qty]'),
          colorName: element.querySelector('[data-addon-color-name]'),
          swatches: [...element.querySelectorAll('[data-addon-color]')],
          quantity: Math.max(1, parseInt(element.dataset.defaultQty, 10) || 1),
          color: variants[0] ? variants[0].color : null,
          colorChosenByShopper: false,
        };
      });

      this.bindEvents();
      this.syncColor(this.currentSofaColor());
      this.render();
    }

    currentSofaColor() {
      const form = this.productForm();
      const input = form && form.querySelector('input[name="id"]');
      const id = input ? Number(input.value) : Number(this.sofa.selected);
      return this.sofa.variants[String(id)] || this.sofa.variants[String(this.sofa.selected)] || null;
    }

    productForm() {
      return (
        this.container.querySelector('product-form form') ||
        document.querySelector(`form[data-type="add-to-cart-form"] input[name="product-id"][value="${this.sofa.productId}"]`)?.form ||
        null
      );
    }

    bindEvents() {
      this.addons.forEach((addon) => {
        addon.checkbox.addEventListener('change', () => this.render());

        addon.swatches.forEach((swatch) => {
          swatch.addEventListener('click', () => {
            addon.color = swatch.dataset.addonColor;
            addon.colorChosenByShopper = true;
            if (!addon.checkbox.checked) addon.checkbox.checked = true;
            this.render();
          });
        });

        addon.element.querySelector('[data-addon-minus]').addEventListener('click', () => {
          addon.quantity = Math.max(1, addon.quantity - 1);
          this.render();
        });
        addon.element.querySelector('[data-addon-plus]').addEventListener('click', () => {
          addon.quantity = Math.min(20, addon.quantity + 1);
          this.render();
        });
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event && event.data && event.data.variant;
          if (!variant || !this.sofaVariantIds.has(Number(variant.id))) return;
          this.syncColor(variant.option1 || (variant.options && variant.options[0]));
          this.render();
        });

      }

      // Runs before the theme's own add-to-cart handler, so the sofa and the add-ons are added together.
      document.addEventListener(
        'submit',
        (event) => {
          const form = this.productForm();
          if (!form || event.target !== form || !this.selected().length) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.addAllToCart(form);
        },
        true
      );
    }

    syncColor(color) {
      if (!color) return;
      this.addons.forEach((addon) => {
        if (addon.colorChosenByShopper) return;
        if (addon.variants.some((variant) => variant.color === color && variant.available !== false)) addon.color = color;
      });
    }

    variantFor(addon) {
      return addon.variants.find((variant) => variant.color === addon.color) || addon.variants[0];
    }

    selected() {
      return this.addons.filter((addon) => addon.checkbox.checked && this.variantFor(addon));
    }

    render() {
      const format = this.sofa.moneyFormat;
      let total = 0;
      let count = 0;

      this.addons.forEach((addon) => {
        const variant = this.variantFor(addon);
        if (!variant) return;
        addon.element.classList.toggle('is-checked', addon.checkbox.checked);
        addon.swatches.forEach((swatch) => {
          const active = swatch.dataset.addonColor === variant.color;
          swatch.classList.toggle('is-selected', active);
          swatch.setAttribute('aria-checked', active ? 'true' : 'false');
        });
        if (addon.colorName) addon.colorName.textContent = variant.color;
        if (addon.qtyLabel) addon.qtyLabel.textContent = String(addon.quantity);
        if (addon.price) addon.price.textContent = formatMoney(variant.price, format);
        if (addon.compare && variant.compare) addon.compare.textContent = formatMoney(variant.compare, format);
        if (addon.image && variant.image) addon.image.src = variant.image;

        if (addon.checkbox.checked) {
          total += variant.price * addon.quantity;
          count += addon.quantity;
        }
      });

      this.root.querySelectorAll('[data-addons-group]').forEach((group) => {
        const ticked = [...group.querySelectorAll('[data-addon-check]')].filter((checkbox) => checkbox.checked).length;
        const badge = group.querySelector('[data-group-count]');
        if (!badge) return;
        badge.hidden = ticked === 0;
        badge.textContent = `${ticked} added`;
      });

      if (this.summary) {
        this.summary.hidden = count === 0;
        this.summary.textContent = count
          ? `Adding ${count} add-on${count === 1 ? '' : 's'} with your sofa: +${formatMoney(total, format)}`
          : '';
      }
      this.container.classList.toggle('bundle-addons-active', count > 0);
      document.body.classList.toggle('bundle-addons-active', count > 0);
    }

    showError(message) {
      if (!this.error) return;
      this.error.hidden = !message;
      this.error.textContent = message || '';
    }

    setLoading(form, loading) {
      const productForm = form.closest('product-form');
      const button = (productForm || form).querySelector('[type="submit"]');
      const spinner = (productForm || form).querySelector('.loading__spinner');
      if (button) {
        button.classList.toggle('loading', loading);
        if (loading) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      }
      if (spinner) spinner.classList.toggle('hidden', !loading);
    }

    showFormError(form, message) {
      const wrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      const text = wrapper?.querySelector('.product-form__error-message');
      if (wrapper) wrapper.toggleAttribute('hidden', !message);
      if (text) text.textContent = message || '';
      this.showError(message ? 'Nothing was added. Please try again.' : '');
    }

    async addAllToCart(form) {
      const button = form.querySelector('[type="submit"]') || form.closest('product-form')?.querySelector('[type="submit"]');
      if (button && button.getAttribute('aria-disabled') === 'true') return;

      const formData = new FormData(form);
      const sofaId = Number(formData.get('id'));
      const sofaQuantity = Math.max(1, parseInt(formData.get('quantity'), 10) || 1);
      if (!sofaId) return;

      const chosen = this.selected();
      const items = [
        { id: sofaId, quantity: sofaQuantity, properties: { [PARENT_PROPERTY]: 'true' } },
        ...chosen.map((addon) => ({
          id: this.variantFor(addon).id,
          quantity: addon.quantity,
          properties: { [ADDON_PROPERTY]: 'true' },
        })),
      ];

      const cartDrawer = document.querySelector('cart-drawer');
      const body = { items };
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (typeof cartDrawer.setActiveElement === 'function') cartDrawer.setActiveElement(document.activeElement);
      }

      this.showFormError(form, '');
      this.setLoading(form, true);
      try {
        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const response = await fetch(`${addUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (data.status) {
          console.error('[Bundle add-ons] add to cart failed', data);
          this.showFormError(form, data.description || data.message || 'Could not add to cart');
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: String(sofaId), cartData: data });
        }

        if (cartDrawer && data.sections && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(data);
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
      } catch (e) {
        console.error('[Bundle add-ons] add to cart failed', e);
        this.showFormError(form, 'Could not add to cart. Please try again.');
      } finally {
        this.setLoading(form, false);
      }
    }
  }

  const init = () => {
    document.querySelectorAll('[data-bundle-addons]').forEach((root) => {
      if (root.bundleAddons) return;
      root.bundleAddons = new BundleAddons(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
